<template>
  <div class="text-gray-700">
    <h1 class="text-2xl font-bold text-gray-900 mb-1">Terms and Conditions</h1>
    <p class="text-sm text-gray-500 mb-6">LayUp Technologies Proprietary Limited · Reg: 2017/290648/07</p>

    <p class="mb-6 text-sm leading-relaxed">
      Please ensure that you have read this User Agreement carefully. By accessing the LayUp website or using our
      services, you agree to be bound by 1) this User Agreement; 2) the Website Terms of Use; and 3) our Website POPI
      Policy. If you do not agree with this User Agreement, please do not access our website or use our services. You
      should print a copy of this User Agreement for your records.
    </p>

    <!-- 1 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">1.</span> Definitions and Interpretation
      </h2>
      <p class="clause">
        1.1. In this Agreement, clause headings are for convenience and shall not be used in its interpretation and,
        unless the context clearly indicates a contrary intention.
      </p>
      <p class="clause">1.2. A word or an expression which denotes –</p>
      <p class="sub-clause">1.2.1. any gender includes the other genders;</p>
      <p class="sub-clause">1.2.2. a natural person includes an artificial or juristic person and vice versa; and</p>
      <p class="sub-clause">1.2.3. the singular includes the plural and vice versa.</p>
      <p class="clause">
        1.3. The following words and expressions shall bear the meanings assigned to them below and cognate words and
        expressions bear corresponding meanings:
      </p>
      <p class="sub-clause">
        1.3.1. <strong>"Agreement"</strong> means this User Agreement, together with annexures (if any), as amended from
        time to time;
      </p>
      <p class="sub-clause">
        1.3.2. <strong>"Applicable Laws"</strong> means in relation to either Party, will include all and any statutes
        and subordinate legislation and common law; regulations; ordinances and by-laws; circulars, codes of practice,
        directives, guidance notices, judgments and decisions of any competent authority or any governmental,
        intergovernmental or supranational agency, body, department or regulatory, self-regulatory or other authority or
        organisation; and other similar provisions, from time to time, compliance with which is mandatory for that
        Party;
      </p>
      <p class="sub-clause">
        1.3.3. <strong>"Business Day"</strong> means any day which is not a Saturday, a Sunday or an official public
        holiday in South Africa;
      </p>
      <p class="sub-clause">
        1.3.4. <strong>"Confidential Information"</strong> means all information, whether proprietary or not, which
        includes but is not limited to: Intellectual Property; commercial and/or financial concepts, specifications,
        data, formulae, methodologies, forecasts and marketing plans, trade secrets, client lists, proprietary
        documentation; all information relating to the business conducted by either Party and their respective clients
        that is reasonably regarded as confidential; any improvement to the aforementioned information; research and
        development information, business records, financial statements, budgets, prices, costs and financial
        projections, names and details of consumers, trade connections, clients and agents, employee details or any
        other information which either Party has an interest in keeping confidential;
      </p>
      <p class="sub-clause">
        1.3.5. <strong>"Parties"</strong> means collectively, You and LayUp and <strong>"Party"</strong> shall mean
        either one, as the context may require;
      </p>
      <p class="sub-clause">
        1.3.6. <strong>"LayUp Service"</strong> means our payment technology, which is designed to allow you to purchase
        products and services from LayUp's partnered merchants through the payment of a deposit followed by instalment
        payments thereafter;
      </p>
      <p class="sub-clause">1.3.7. <strong>"South Africa"</strong> means the Republic of South Africa.</p>
      <p class="clause">
        1.4. Any reference to any statute, regulation or other legislation shall be a reference to that statute,
        regulation or other legislation as at the Signature Date, and as amended or substituted from time to time.
      </p>
      <p class="clause">
        1.5. If any provision in a definition is a substantive provision conferring a right or imposing an obligation on
        either Party then, notwithstanding that it is only in a definition, effect shall be given to that provision as
        if it were a substantive provision in the body of this Agreement.
      </p>
      <p class="clause">
        1.6. Where any term is defined within a particular clause other than this clause 1, that term shall bear the
        meaning ascribed to it in that clause wherever it is used in this Agreement.
      </p>
      <p class="clause">
        1.7. Where any number of days is to be calculated from a particular day, such number shall be calculated as
        excluding such particular day and commencing on the next day. If the last day falls on a day which is not a
        Business Day, the last day shall be deemed to be the next succeeding Business Day.
      </p>
      <p class="clause">
        1.8. Reference to days (other than Business Days), months or years shall refer to calendar days, months or
        years, as the case may be.
      </p>
      <p class="clause">
        1.9. Any term which refers to a South African legal concept or process shall be deemed to include a reference to
        the equivalent or analogous concept or process in any other jurisdiction in which this Agreement may apply.
      </p>
      <p class="clause">
        1.10. Any word, term or expression defined in this Agreement shall bear the same meanings in any annexure
        hereto, unless it contains its own definition or the context indicates otherwise.
      </p>
      <p class="clause">
        1.11. The use of the word "including", "includes" or "include" followed by specific examples shall not be
        construed as limiting the meaning of the general wording preceding it.
      </p>
    </div>

    <!-- 2 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">2.</span> Introduction</h2>
      <p class="clause">
        2.1. This Agreement describes the relationship between you ("you" or "your") and LayUp ("we", "us", "our")
        regarding your use of the LayUp services, as a consumer to purchase from our partnered merchants.
      </p>
      <p class="clause">
        2.2. We may unilaterally decide to change this Agreement from time to time, provided that such changes will not
        impose additional obligations on you concerning actions you had taken before the change became effective, unless
        you specifically agree to such changes. If we make any changes that we deem to be material, we will make a
        reasonable effort to inform you of such changes, but it is your responsibility to review the Agreement posted on
        our website from time to time.
      </p>
      <p class="clause">
        2.3. Using the LayUp Service means that you must accept all of the terms and conditions contained in this
        Agreement.
      </p>
    </div>

    <!-- 3 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">3.</span> Eligibility</h2>
      <p class="clause">
        To be eligible to use the LayUp Service, you must be at least 18 (eighteen) years old and a resident of South
        Africa or those territories within which we may elect to conduct business from time to time. You represent and
        warrant that you are eligible to use the LayUp Service.
      </p>
    </div>

    <!-- 4 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">4.</span> Our Service</h2>
      <p class="clause">
        4.1. Prior to completing any transaction on your behalf through any LayUp Service, we will secure your promise
        to pay by disclosing all terms and conditions applicable to that transaction and securing your affirmative
        consent to those terms, including your promise to pay the amount of the good/s or service/s. You give us the
        right to collect any unpaid portions of the financed purchase, plus any agreed upon additional fees incurred.
      </p>
      <p class="clause">
        4.2. To become a user of the LayUp Service, you must create a LayUp user profile. By opening a user account and
        accepting the terms as outlined in this Agreement, you attest that you are establishing the user account to
        enable yourself to finance the purchase of goods and services from our partnered merchants.
      </p>
      <p class="clause">
        4.3. When creating a user profile, we will require that you provide us with certain information (some of which
        is optional), including but not limited to, your email address, full names and cell phone number. After you have
        created the user profile, we will send you an activation link via email.
      </p>
    </div>

    <!-- 5 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">5.</span> Our Role</h2>
      <p class="clause">
        5.1. We are not a party to any sale of goods or services purchased by you from one of our partnered merchants.
      </p>
      <p class="clause">
        5.2. We do not have control of, or liability for, the products or services purchased through the LayUp Service.
        We make no warranty in relation to the quality of goods or the level of service provided by our partnered
        merchants — it is therefore crucial that you familiarise yourself with their terms and conditions. If you have a
        dispute or query related to your purchase please send an email to
        <a href="mailto:customersupport@layup.co.za" class="text-brand-700 underline">customersupport@layup.co.za</a>
        and we will assist with getting you feedback.
      </p>
    </div>

    <!-- 6 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">6.</span> Cancelling Transactions
      </h2>
      <p class="clause">
        The cancellation of any payment plan created through the LayUp Service is subject to the respective merchant's
        terms and conditions, as well as their cancellation policy. The cancellation fee (if any) charged by each
        merchant is communicated to you on the payment plan widget before you activate the payment plan. Please ensure
        that you are familiar with the cancellation policy of the particular merchant you are transacting with as we
        bear no liability or responsibility in this regard.
      </p>
    </div>

    <!-- 7 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">7.</span> Payment</h2>
      <p class="clause">
        Payment methods for your deposit and subsequent payments may include but are not limited to automated debit
        orders, credit/debit cards, and instant EFT. You agree that you grant authorization to LayUp to make the
        transfers from any of your bank account(s), debit card(s) and/or credit card(s) to fulfil your deposit payment,
        or a missed or failed payment. You give LayUp the right to resubmit any transaction you authorized that is
        returned for insufficient or uncollected funds or any other reason.
      </p>
    </div>

    <!-- 8 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">8.</span> Fees and Interest
      </h2>
      <p class="clause">
        We charge no interest and no up-front fees to users for making purchases via the LayUp Service. We reserve the
        right to add interest and fees to future purchases should our business model change. We will notify users of all
        fees and interest if and when we decide to add them. This is a material change to the terms and conditions of
        this Agreement, therefore you will be notified accordingly and be afforded an opportunity to agree to these
        amended terms to the extent that may be necessary.
      </p>
    </div>

    <!-- 9 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">9.</span> Refunds</h2>
      <p class="clause">
        The refund process applicable to you shall be dealt with in terms of the terms and conditions agreed to between
        yourself and the applicable LayUp merchant. All refund requests will need to be initiated with the merchant who
        will in turn instruct us to process relevant refunds. LayUp will only be responsible for facilitating refunds in
        the event where we are holding funds on behalf of the merchant. Please be advised that LayUp will always refund
        back to the source of the original payment.
      </p>
    </div>

    <!-- 10 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">10.</span> Payment Review
      </h2>
      <p class="clause">
        We have a payment review process whereby we review certain potentially high-risk transactions made by you. If a
        payment is subject to our payment review process, we will place a hold on the payment and provide notice to you
        and the relevant merchant. LayUp will conduct a review and either clear or cancel the payment. If the payment is
        cleared, LayUp will provide notice to you and the merchant. Otherwise, LayUp will cancel the payment and the
        funds will be returned to you.
      </p>
    </div>

    <!-- 11 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">11.</span> Unauthorized Payment
      </h2>
      <p class="clause">
        If your problem is a LayUp transaction that you did not authorize, please contact LayUp at
        <a href="mailto:customersupport@layup.co.za" class="text-brand-700 underline">customersupport@layup.co.za</a>
        to report the unauthorized transaction. For LayUp to help you, you must have a LayUp user profile and be in good
        standing, have purchased an item via LayUp, and be on time with all instalment payments to date. You must also
        lodge the dispute within 30 (thirty) days of the date of the payment.
      </p>
    </div>

    <!-- 12 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">12.</span> Your Bank Accounts
      </h2>
      <p class="clause">
        When you log into your bank account to connect your bank account(s) to your LayUp account, LayUp will store
        information related to all accounts connected to this bank connection. LayUp reserves the right to initiate
        payments and withdraw funds from any bank account(s) on file at any time in order to collect all payments,
        including delinquent payments. This includes saving accounts and non-primary checking accounts.
      </p>
    </div>

    <!-- 13 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">13.</span> Credit Reports
      </h2>
      <p class="clause">
        If you open a LayUp account, you are providing LayUp with express authorization to obtain your personal credit
        report from a credit bureau. We would use this credit report as a supplement to our 'Know Your Customer'
        processes and to identify any business risks that may exist to LayUp in offering financing to you.
      </p>
    </div>

    <!-- 14 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">14.</span> Proprietary Analysis
      </h2>
      <p class="clause">
        We use proprietary analysis tools to evaluate your ability to successfully complete your instalment payments
        with LayUp. LayUp reserves the right to alter its analysis algorithms at any time.
      </p>
    </div>

    <!-- 15 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">15.</span> Your Privacy</h2>
      <p class="clause">
        15.1. Protecting your privacy is very important to LayUp. Please review our Privacy Policy to better understand
        our commitment to maintaining your privacy, as well as our use and disclosure of your Information.
      </p>
      <p class="clause">
        15.2. You are responsible for maintaining adequate security and control of any and all IDs, OTPs, passwords,
        personal identification numbers (PINs), or any other codes that you use to access the LayUp Services.
      </p>
      <p class="clause">
        15.3. You authorize LayUp, directly or through third parties, to make any inquiries we consider necessary to
        validate your identity. We may ask for your identity number or to see your driver's license or other identifying
        documents at any time. LayUp reserves the right to close, suspend, or limit access to your LayUp account and all
        LayUp Services in the event we are unable to obtain or verify this Information.
      </p>
    </div>

    <!-- 16 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">16.</span> Liability</h2>
      <p class="clause">
        16.1. You are responsible for all fees, fines, penalties and other liability incurred by LayUp, a user, a LayUp
        Merchant, or a third party caused by or arising out of your breach of this Agreement, and your use of the LayUp
        Service. You agree to reimburse LayUp, a user, or a third party for any and all such liability.
      </p>
      <p class="clause">
        16.2. If you are liable for any amounts owed to LayUp, you authorize LayUp to process and collect such amounts
        from your bank account or credit/debit card on file. If you do not have sufficient funds in your bank account,
        you acknowledge that we may engage in collection efforts to recover such amounts from you.
      </p>
    </div>

    <!-- 17 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">17.</span> Indemnity</h2>
      <p class="clause">
        17.1. IN NO EVENT SHALL LAYUP, ITS AFFILIATES, DIRECTORS, AGENTS, JOINT VENTURERS, EMPLOYEES AND SUPPLIERS BE
        LIABLE FOR LOST PROFITS OR ANY SPECIAL, INCIDENTAL OR CONSEQUENTIAL DAMAGES (INCLUDING WITHOUT LIMITATION
        DAMAGES FOR LOSS OF DATA OR LOSS OF BUSINESS) ARISING OUT OF OR IN CONNECTION WITH OUR WEBSITE, THE LAYUP
        SERVICES, OR THIS AGREEMENT (HOWEVER ARISING, INCLUDING NEGLIGENCE), UNLESS AND TO THE EXTENT PROHIBITED BY LAW.
        OUR LIABILITY TO YOU OR ANY THIRD PARTIES IN ANY CIRCUMSTANCE IS LIMITED TO THE ACTUAL AMOUNT OF DIRECT DAMAGES.
        YOU AGREE NOT TO HOLD THESE PARTIES RESPONSIBLE FOR ANY DAMAGES OR LOSSES RESULTING DIRECTLY OR INDIRECTLY FROM:
      </p>
      <p class="sub-clause">17.1.1. YOUR USE OF OR YOUR INABILITY TO USE LAYUP'S SITES AND SERVICES;</p>
      <p class="sub-clause">17.1.2. DELAYS OR DISRUPTIONS IN LAYUP'S SITES AND THE LAYUP SERVICE;</p>
      <p class="sub-clause">
        17.1.3. VIRUSES OR OTHER MALICIOUS SOFTWARE OBTAINED BY ACCESSING LAYUP'S SITES OR SERVICES OR ANY SITE OR
        SERVICE LINKED TO LAYUP'S SITES OR SERVICES;
      </p>
      <p class="sub-clause">
        17.1.4. GLITCHES, BUGS, ERRORS, OR INACCURACIES OF ANY KIND IN LAYUP'S SITES OR SERVICES OR IN THE INFORMATION
        AND GRAPHICS OBTAINED FROM THEM;
      </p>
      <p class="sub-clause">17.1.5. THE CONTENT, ACTIONS, OR INACTIONS OF THIRD PARTIES;</p>
      <p class="sub-clause">17.1.6. A SUSPENSION OR OTHER ACTION TAKEN WITH RESPECT TO YOUR ACCOUNT;</p>
      <p class="sub-clause">
        17.1.7. YOUR NEED TO MODIFY PRACTICES, CONTENT, OR BEHAVIOR, OR YOUR LOSS OF OR INABILITY TO DO BUSINESS, AS A
        RESULT OF CHANGES TO THIS AGREEMENT OR LAYUP'S POLICIES.
      </p>
      <p class="clause">
        17.2. LAYUP SHALL UNDER NO CIRCUMSTANCES ASSUME ANY LIABILITY, AND YOU INDEMNIFY LAYUP AGAINST ANY CLAIM FOR
        LATE OR LACK OF DELIVERY AS WELL AS ANY DEFECT IN OR DAMAGE CAUSED BY A GOOD/S OR SERVICE/S WHICH YOU PURCHASED
        USING THE LAYUP TECHNOLOGY, INCLUDING BUT NOT LIMITED TO ANY REFUNDS, REPAIRS OR REPLACEMENTS OF THE GOODS IN
        ACCORDANCE WITH RELEVANT LAWS OR MANUFACTURERS' WARRANTIES.
      </p>
    </div>

    <!-- 18 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">18.</span> No Warranties</h2>
      <p class="clause">
        18.1. The LayUp Service is provided without any representation or warranty, whether express, implied, tacit or
        statutory. LayUp, our affiliates, and the officers, directors, agents, joint venturers, employees and suppliers
        of LayUp specifically disclaim any implied warranties of title, merchantability, fitness for a particular
        purpose and non-infringement.
      </p>
      <p class="clause">
        18.2. LayUp does not have any control over the products or services that are paid for with the LayUp Service and
        LayUp cannot ensure that the merchant you are transacting with will complete the transaction or is authorized to
        do so. LayUp does not guarantee continuous, uninterrupted or secure access to any part of the LayUp Service, and
        operation of our site may be interfered with by numerous factors outside of our control.
      </p>
    </div>

    <!-- 19 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">19.</span> Restricted Activities
      </h2>
      <p class="clause">
        19.1. With regard to your use of our website, your account, the LayUp Service, or in the course of your
        interactions with us, other users, or third parties, you agree and undertake that you will not:
      </p>
      <p class="sub-clause">
        19.1.1. Breach this Agreement or any other agreement or policy that you have entered into with us;
      </p>
      <p class="sub-clause">19.1.2. Violate any Applicable Laws;</p>
      <p class="sub-clause">19.1.3. Infringe upon LayUp's or any third party's Confidential Information;</p>
      <p class="sub-clause">
        19.1.4. Act in a manner that is defamatory, threatening or harassing to LayUp, LayUp users, merchants or any
        third party;
      </p>
      <p class="sub-clause">
        19.1.5. Provide false, inaccurate or misleading information to LayUp and/or LayUp users and/or the general
        public;
      </p>
      <p class="sub-clause">19.1.6. Send or receive what we reasonably believe to be potentially fraudulent funds;</p>
      <p class="sub-clause">
        19.1.7. Refuse to cooperate in an investigation or provide confirmation of your identity or any information you
        provide to us;
      </p>
      <p class="sub-clause">
        19.1.8. Conduct your business or use the LayUp Service in a manner that results in or may result in complaints,
        disputes, fees, fines, penalties or other liability to LayUp, users, third parties or you;
      </p>
      <p class="sub-clause">
        19.1.9. Circumvent any LayUp policy or determinations about your account such as temporary or indefinite
        suspensions or other limitations or restrictions, including attempting to create new or additional LayUp
        account(s) when an account has been restricted, suspended or otherwise limited; creating new or additional LayUp
        accounts using information that is not your own; or using someone else's LayUp account.
      </p>
    </div>

    <!-- 20 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3">
        <span class="shrink-0">20.</span> Actions by LayUp
      </h2>
      <p class="clause">
        20.1. If we, in our sole and absolute discretion, believe that you may have engaged in any restricted activities
        as listed in clause 19, we may take various actions to protect LayUp, users, other third parties, or you from
        fees, fines, penalties and any other liability. The actions we may take include, but are not limited to, the
        following:
      </p>
      <p class="sub-clause">
        20.1.1. closing, suspending, or limiting your access to your account or LayUp Service (such as limiting your
        ability to accept payments, make withdrawals, or remove financial information);
      </p>
      <p class="sub-clause">
        20.1.2. contacting merchants you have interacted with, contacting your bank, and/or warning other users, law
        enforcement, or impacted third parties of your actions;
      </p>
      <p class="sub-clause">20.1.3. updating inaccurate information you provided us;</p>
      <p class="sub-clause">20.1.4. refusing you access to the LayUp Service in future;</p>
      <p class="sub-clause">
        20.1.5. holding any balance for up to 180 (one hundred and eighty) days if reasonably needed to protect us
        against the risk of liability or if you have violated any Applicable Laws, or as otherwise directed by any court
        exercising jurisdiction; and
      </p>
      <p class="sub-clause">20.1.6. instituting legal proceedings against you.</p>
      <p class="clause">
        20.2. If we close your account or terminate your use of the LayUp Service for any reason, we will provide you
        with notice of our actions. If we limit access to your account, we will provide you with notice of our actions
        and an opportunity to request restoration of access if, in our sole discretion, we deem it appropriate.
      </p>
    </div>

    <!-- 21 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">21.</span> Notices</h2>
      <p class="clause">
        21.1. You understand and agree that LayUp may, without further notice or warning and in our discretion, monitor
        or record telephone conversations you or anyone acting on your behalf has with LayUp or its agents for quality
        control and training purposes or for its own protection.
      </p>
      <p class="clause">
        21.2. You acknowledge, understand and further agree that, while your communications with LayUp may be overheard,
        monitored, or recorded without further notice or warning, not all telephone lines or calls may be recorded by
        us, and we do not guarantee that recordings of any particular telephone call will be retained or retrievable
        from us.
      </p>
    </div>

    <!-- 22 -->
    <div class="mb-6">
      <h2 class="flex gap-2 text-base font-bold text-gray-900 mb-3"><span class="shrink-0">22.</span> General</h2>
      <p class="clause">
        22.1. This Agreement supersedes and replaces any and all agreements between the Parties and undertakings given
        to or on behalf of the Parties in relation to the subject matter hereof.
      </p>
      <p class="clause">
        22.2. The expiration, cancellation or other termination of this Agreement shall not affect those provisions
        which expressly provide that they will operate after such expiration, cancellation or other termination or which
        of necessity must continue to endure after such termination.
      </p>
      <p class="clause">
        22.3. This Agreement constitutes the entire agreement between the Parties as to the subject matter hereof and no
        agreements, representations or warranties between the Parties regarding the subject matter hereof other than
        those set out herein are binding on the Parties.
      </p>
      <p class="clause">
        22.4. No indulgence, leniency or extension of time which any Party may give or allow to the other Party in
        respect of the performance of any obligation hereunder shall in any way prejudice the Party giving or allowing
        the indulgence or preclude such Party from exercising any of its rights in terms of this Agreement.
      </p>
      <p class="clause">
        22.5. No addition to, alteration, cancellation, variation or novation of this Agreement and no waiver of any
        right arising from this Agreement or its breach or termination shall be of any force or effect unless reduced to
        writing and signed by all the Parties or their duly authorised representatives.
      </p>
      <p class="clause">
        22.6. Each of the provisions of this Agreement is separate and severable and enforceable accordingly. If any
        such term or condition is or becomes unenforceable for any reason whatsoever, that term or condition is
        severable from and shall not affect the validity of any other term or condition contained in this Agreement.
      </p>
      <p class="clause">
        22.7. You shall not be entitled to cede, assign or delegate any of your rights and/or obligations in terms of or
        arising from this Agreement to any third party without the prior written consent of LayUp.
      </p>
      <p class="clause">22.8. This Agreement shall be governed by the laws of the Republic of South Africa.</p>
    </div>
  </div>
</template>

<script setup lang="ts"></script>
